// BookingStatsBar.js — full-width clickable stat tiles for booking status filter (like ProjectStatsBar)
import React from 'react';
import { Box, Typography } from '@mui/material';

const T = {
  white: '#ffffff',
  border: '#e0ece4',
  ink: '#0f1f15',
  subtle: '#7a9585',
  g50: '#eef7f1',
  mint: '#0a6b3e',
  // Per-status number colors (inactive text)
  draft: '#475569',
  pending: '#92400e',
  reserved: '#5b21b6',
  confirmed: '#15803d',
  cancelled: '#991b1b',
  completed: '#065f46',
  expired: '#374151',
};

const STAT_ITEMS = [
  { key: 'all', label: 'Total', filter: 'all', numColor: T.mint },
  { key: 'draft', label: 'Draft', filter: 'draft', numColor: T.draft },
  { key: 'pending', label: 'Pending', filter: 'pending', numColor: T.pending },
  { key: 'reserved', label: 'Reserved', filter: 'reserved', numColor: T.reserved },
  { key: 'confirmed', label: 'Confirmed', filter: 'confirmed', numColor: T.confirmed },
  { key: 'cancelled', label: 'Cancelled', filter: 'cancelled', numColor: T.cancelled },
  { key: 'completed', label: 'Completed', filter: 'completed', numColor: T.completed },
  { key: 'expired', label: 'Expired', filter: 'expired', numColor: T.expired },
];

export default function BookingStatsBar({ statusCounts = {}, loading, onFilterChange, activeFilter = 'all' }) {
  const getCount = (key) => {
    if (key === 'all') return statusCounts.all?.count ?? 0;
    const item = statusCounts[key];
    return item?.count ?? 0;
  };

  const getLabel = (key, fallback) => {
    if (key === 'all') return statusCounts.all?.label || fallback;
    const item = statusCounts[key];
    const label = item?.label || fallback;
    return (typeof label === 'string' ? label : fallback).replace(/\s*Approval\s*$/i, '').trim();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        mb: '12px',
      }}
    >
      {STAT_ITEMS.map(({ key, label, filter, numColor }, idx) => {
        const isActive = (activeFilter || 'all') === filter;
        const isLast = idx === STAT_ITEMS.length - 1;
        const count = getCount(key);
        const displayLabel = getLabel(key, label);
        return (
          <Box
            key={key}
            onClick={() => onFilterChange?.(filter)}
            sx={{
              flex: 1,
              minWidth: 0,
              px: '14px',
              py: '12px',
              cursor: 'pointer',
              textAlign: 'center',
              borderRight: isLast ? 'none' : `1px solid ${T.border}`,
              borderBottom: isActive ? `3px solid ${T.mint}` : '3px solid transparent',
              background: isActive ? T.g50 : T.white,
              transition: 'background 150ms ease, border-color 150ms ease',
              '&:hover': { background: T.g50 },
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 700,
                color: loading ? T.subtle : isActive ? T.mint : T.ink,
                lineHeight: 1,
              }}
            >
              {loading ? '—' : count}
            </Typography>
            <Typography
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: isActive ? T.mint : T.subtle,
                mt: '2px',
              }}
            >
              {displayLabel}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
